import { Link } from 'react-router-dom';

const guides = [
  {
    slug: '/article/cssbuy-spreadsheet-ultimate-guide',
    tag: 'Featured Guide',
    tagColor: 'bg-accent-100 text-accent-800',
    title: 'Ultimate CSSBuy Spreadsheet Guide 2026',
    description: 'Everything you need to know about finding the best CSSBuy Spreadsheet — from beginner basics to advanced seller verification techniques.',
    highlights: [
      'How to find verified sellers on CSSBuy',
      'Complete QC photo inspection checklist',
      'Top 100 trusted seller directory',
    ],
    image: 'https://readdy.ai/api/search-image?query=Clean%20minimalist%20workspace%20with%20laptop%20showing%20spreadsheet%20data%20and%20fashion%20product%20images%2C%20warm%20beige%20and%20cream%20tones%2C%20soft%20natural%20lighting%20from%20window%2C%20modern%20cozy%20aesthetic%2C%20organized%20desk%20with%20notebook%20and%20coffee%2C%20editorial%20photography%20style%20with%20gentle%20shadows%2C%20lifestyle%20productivity%20scene&width=800&height=500&seq=featured-guide-2026-x2&orientation=landscape',
    imageOverlay: 'New for 2026',
    overlayText: 'Updated daily with the latest seller reviews and product listings',
  },
  {
    slug: '/article/cssbuy-vs-kakobuy-comparison',
    tag: 'Comparison',
    tagColor: 'bg-primary-100 text-primary-800',
    title: 'CSSBuy vs Kakobuy — Which Platform Wins?',
    description: 'Side-by-side comparison of pricing, shipping, QC quality, seller ecosystems, and community support to help you choose the right platform.',
    highlights: [
      'Detailed fee comparison across both platforms',
      'QC photo quality and quantity breakdown',
      'Spreadsheet ecosystem maturity analysis',
    ],
    image: 'https://readdy.ai/api/search-image?query=Split%20screen%20comparison%20concept%20with%20two%20laptops%20side%20by%20side%20showing%20ecommerce%20dashboards%2C%20warm%20beige%20and%20cream%20tones%2C%20clean%20modern%20desk%20setup%2C%20soft%20natural%20window%20light%2C%20minimalist%20workspace%20aesthetic%2C%20professional%20comparison%20theme%2C%20gentle%20shadows&width=800&height=500&seq=featured-comparison-2026-x2&orientation=landscape',
    imageOverlay: 'In-Depth Analysis',
    overlayText: 'Make an informed decision before your next purchase',
  },
  {
    slug: '/article/cssbuy-shipping-guide',
    tag: 'Guide',
    tagColor: 'bg-secondary-100 text-secondary-800',
    title: 'CSSBuy Shipping Guide — Methods, Costs & Tips',
    description: 'Complete shipping resource covering every carrier, cost estimation, customs procedures, packaging strategies, and regional delivery timeframes.',
    highlights: [
      'Carrier comparison with real cost estimates',
      'Customs & duties guide by country',
      'Packaging tips to reduce shipping costs',
    ],
    image: 'https://readdy.ai/api/search-image?query=International%20shipping%20logistics%20concept%20with%20cardboard%20boxes%20and%20packing%20materials%20on%20clean%20modern%20desk%2C%20world%20map%20background%2C%20warm%20beige%20cream%20tones%2C%20soft%20natural%20lighting%2C%20organized%20workspace%20aesthetic%2C%20delivery%20and%20logistics%20theme%2C%20gentle%20shadows&width=800&height=500&seq=featured-shipping-2026-x2&orientation=landscape',
    imageOverlay: 'Save on Shipping',
    overlayText: 'Learn how to cut shipping costs by 30-50%',
  },
  {
    slug: '/article/cssbuy-seller-directory',
    tag: 'Directory',
    tagColor: 'bg-accent-100 text-accent-800',
    title: 'CSSBuy Seller Directory — 500+ Verified Sellers',
    description: 'Your guide to trusted sellers across every category. Learn our verification process, spot red flags, and discover category specialists.',
    highlights: [
      '500+ community-vetted sellers',
      '6 category specialists with ratings',
      'Red flags checklist to avoid scams',
    ],
    image: 'https://readdy.ai/api/search-image?query=Curated%20marketplace%20directory%20concept%20with%20product%20catalog%20cards%20arranged%20on%20warm%20beige%20desk%2C%20fashion%20and%20accessory%20items%20displayed%2C%20soft%20natural%20lighting%2C%20clean%20organized%20workspace%2C%20trustworthy%20seller%20theme%2C%20warm%20cream%20tones%2C%20editorial%20product%20photography%20with%20gentle%20shadows&width=800&height=500&seq=featured-directory-2026-x2&orientation=landscape',
    imageOverlay: 'Verified Sellers',
    overlayText: 'Every seller reviewed by our community before listing',
  },
];

export default function FeaturedArticle() {
  return (
    <section className="py-14 md:py-16 bg-background-50">
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        <div className="text-center mb-10">
          <p className="text-xs uppercase tracking-[0.2em] text-foreground-500 font-medium mb-3">Latest Guides</p>
          <h2 className="font-heading text-3xl md:text-4xl font-bold text-foreground-900 leading-tight">
            In-Depth CSSBuy Spreadsheet Resources
          </h2>
          <p className="text-foreground-600 text-sm mt-3 max-w-xl mx-auto">
            Comprehensive guides to help you shop smarter, ship cheaper, and find the best sellers
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {guides.map((guide) => (
            <Link
              key={guide.slug}
              to={guide.slug}
              className="group bg-background-100 border border-background-200/70 rounded-lg overflow-hidden hover:border-primary-300 transition-all duration-300 cursor-pointer flex flex-col h-full"
            >
              <div className="relative aspect-[16/9] overflow-hidden">
                <img
                  src={guide.image}
                  alt={guide.title}
                  className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent"></div>
                <div className="absolute top-3 left-3">
                  <span className={`inline-block px-3 py-1 ${guide.tagColor} text-xs font-semibold rounded-full`}>
                    {guide.tag}
                  </span>
                </div>
                <div className="absolute bottom-0 left-0 right-0 p-4">
                  <span className="inline-block px-2.5 py-1 bg-background-50/90 text-foreground-800 text-xs font-medium rounded-full mb-1.5">
                    {guide.imageOverlay}
                  </span>
                  <p className="text-background-50 text-xs opacity-90">{guide.overlayText}</p>
                </div>
              </div>
              <div className="p-5 flex flex-col flex-1">
                <h3 className="font-heading text-lg font-bold text-foreground-900 mb-2 group-hover:text-primary-600 transition-colors leading-snug">
                  {guide.title}
                </h3>
                <p className="text-sm text-foreground-600 leading-relaxed mb-4 flex-1">
                  {guide.description}
                </p>
                <ul className="space-y-1.5 mb-4">
                  {guide.highlights.map((item) => (
                    <li key={item} className="flex items-start gap-2 text-xs text-foreground-500">
                      <i className="ri-check-line text-primary-500 mt-0.5 flex-shrink-0"></i>
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
                <div className="flex items-center gap-1 text-sm font-semibold text-primary-600 group-hover:gap-2 transition-all whitespace-nowrap">
                  Read Full Guide
                  <i className="ri-arrow-right-line text-xs"></i>
                </div>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}